import type { CompanyWithCounts } from "@dutyon/apis";
import { getCompanies, mapClientError, queryKeys, updateCompany } from "@dutyon/apis";
import {
  Button,
  Card,
  Loader,
  LoadingOverlay,
  modals,
  notifications,
  Stack,
  Switch,
  Table,
  Text,
  TitleContainer,
} from "@dutyon/ui-kit";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

export function CompanyListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    data: companies = [],
    isLoading,
    isFetching,
  } = useQuery<CompanyWithCounts[]>({
    queryKey: queryKeys.companies.all,
    queryFn: () => getCompanies(),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      updateCompany({ id, data: { dutyAutoGenerationEnabled: enabled } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.companies.all });
      notifications.show({
        title: "자동 생성 권한 변경 완료",
        message: "병원의 Duty 자동 생성 기능 활성화 여부를 변경했습니다.",
        color: "green",
      });
    },
    onError: (error: unknown) => {
      notifications.show({
        title: "자동 생성 권한 변경 실패",
        message: mapClientError(error, {
          fallback: "Duty 자동 생성 기능 활성화 여부를 변경하지 못했습니다.",
        }).message,
        color: "red",
      });
    },
  });

  const handleToggleAutoGeneration = (companyId: string, companyName: string, enabled: boolean) => {
    modals.openConfirmModal({
      title: enabled ? "Duty 자동 생성 기능 활성화" : "Duty 자동 생성 기능 비활성화",
      centered: true,
      children: (
        <Text size="sm">
          <strong>{companyName}</strong> 병원의 Duty 자동 생성 기능을{" "}
          {enabled ? "활성화" : "비활성화"}하시겠습니까?
        </Text>
      ),
      labels: { confirm: enabled ? "활성화" : "비활성화", cancel: "취소" },
      confirmProps: { color: enabled ? "blue" : "orange" },
      onConfirm: () => toggleMutation.mutate({ id: companyId, enabled }),
    });
  };

  const formatDate = (dateString: Date | string) => {
    return new Date(dateString).toLocaleDateString("ko-KR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const rows = companies.map((company) => (
    <Table.Tr key={company.id}>
      <Table.Td>{company.name}</Table.Td>
      <Table.Td>{company.code}</Table.Td>
      <Table.Td>{company._count?.teams ?? 0}개</Table.Td>
      <Table.Td>{company._count?.nurses ?? 0}명</Table.Td>
      <Table.Td>
        <Switch
          checked={company.dutyAutoGenerationEnabled ?? false}
          aria-label={`${company.name} Duty 자동 생성 활성화`}
          disabled={toggleMutation.isPending}
          onChange={(event) =>
            handleToggleAutoGeneration(company.id, company.name, event.currentTarget.checked)
          }
        />
      </Table.Td>
      <Table.Td>{formatDate(company.createdAt)}</Table.Td>
      <Table.Td>{formatDate(company.updatedAt)}</Table.Td>
      <Table.Td>
        <Button size="xs" onClick={() => navigate(`/companies/${company.id}`)}>
          상세보기
        </Button>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <TitleContainer
      title="병원 관리"
      rightSection={<Button onClick={() => navigate("/companies/create")}>신규 병원 등록</Button>}
    >
      <Card withBorder radius="md" p={0} pos="relative" style={{ overflow: "hidden" }}>
        <LoadingOverlay visible={isLoading || isFetching} overlayProps={{ blur: 1 }} />
        <Table.ScrollContainer minWidth={800}>
          <Table highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>이름</Table.Th>
                <Table.Th>코드</Table.Th>
                <Table.Th>팀 수</Table.Th>
                <Table.Th>사용자 수</Table.Th>
                <Table.Th>자동생성</Table.Th>
                <Table.Th>등록일</Table.Th>
                <Table.Th>수정일</Table.Th>
                <Table.Th>작업</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {isLoading ? (
                <Table.Tr>
                  <Table.Td colSpan={8} align="center" py="xl">
                    <Stack align="center" gap="xs">
                      <Loader size="sm" />
                      <Text size="sm" c="dimmed">
                        병원 목록을 불러오는 중입니다...
                      </Text>
                    </Stack>
                  </Table.Td>
                </Table.Tr>
              ) : companies.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={8} align="center" py="xl">
                    <Text size="sm" c="dimmed">
                      등록된 병원이 없습니다.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                rows
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>
    </TitleContainer>
  );
}
